import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppId, AppThunkAPIType } from "../../../app/appTypes";
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http";
import { DocumentContentOrigin } from "../documentsApi";

export type DqMappingNote = "OK" | "FAIL" | "NA";


export interface DocumentDqMapping {
    id: number;
    documentId: number;
    sectionId: string | null;
    guid: string;
    order: number;
    requirementId: number | null;
    requirementOrigin: DocumentContentOrigin;
    isRequirementFixed: boolean;
    requirementNumber: string;
    requirementDescription: string;
    fsNumber: string;
    fsTitle: string;
    fsOrigin: DocumentContentOrigin;
    mappingComment: string;
    note: DqMappingNote | null;
    fsDescription: string;
    isFsFixed: boolean;
    docMappings: DqMappingDocumentItem[];
}


export interface DqMappingDocumentItem {
    id: number;
    mappingId: number;
    order: number;
    docOrigin: DocumentContentOrigin;
    docNumber: string;
    docTitle: string;
    docDescription: string;
    mappingComment: string;
    isFsFixed: boolean;
}

export const loadDocumentDqMappings = createAsyncThunk<DocumentDqMapping[], { documentId: AppId; sectionId: string }
    , AppThunkAPIType>("document-dq-mappings/loadDocumentDqMappings",
        async ({ documentId, sectionId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(getWithAuth({
                url: `api/document-dq-mappings/${documentId}/sections/${sectionId}`,
            }))
            const { payload } = result
            if (getWithAuth.fulfilled.match(result)) {
                return payload as DocumentDqMapping[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export const generateMappings = createAsyncThunk<DocumentDqMapping[], { documentId: AppId; sectionId: string; overwriteExisting: boolean }
    , AppThunkAPIType>("document-dq-mappings/generarteDocumentDqMappings",
        async ({ documentId, sectionId, overwriteExisting }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-dq-mappings/${documentId}/sections/${sectionId}/generate`,
                payload: { OverwriteExisting: overwriteExisting }
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as DocumentDqMapping[]
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export const prepareDqMappingsGenerationFromAI = createAsyncThunk<boolean, { documentId: AppId; sectionId: string, blobId: string }
    , AppThunkAPIType>("document-dq-mappings/prepareDqMappingsGenerationFromAI",
        async ({ documentId, sectionId, blobId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-dq-mappings/documents/${documentId}/sections/${sectionId}/ai/prepare/${blobId}`,
                payload: {}
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as boolean
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export const generateDqMappingsFromAI = createAsyncThunk<DocumentDqMapping, { documentId: AppId; sectionId: string; mappingId: number; blobId: string }
    , AppThunkAPIType>("document-dq-mappings/generateDqMappingsFromAI",
        async ({ documentId, sectionId, mappingId, blobId }, { dispatch, rejectWithValue }) => {
            const result = await dispatch(postWithAuth({
                url: `api/document-dq-mappings/documents/${documentId}/sections/${sectionId}/mappings/${mappingId}/ai/generate/${blobId}`,
                payload: {}
            }))
            const { payload } = result
            if (postWithAuth.fulfilled.match(result)) {
                return payload as DocumentDqMapping
            } else {
                return rejectWithValue(payload ?? { kind: 'unknown' })
            }
        })

export type DocumentDqMappingsAiGenerationScope = "ALL" | "WITHOUT_MAPPINGS" | "SELECTED";

export interface OrderDocumentDqMappingsAiGenerationRequestDto {
    fileId: string;
    sectionId: string | null;
    documentId: number;
    scope: DocumentDqMappingsAiGenerationScope;
    mappingIds: number[];
}

export const runDocumentDqMappingsAiGenerationProcess = createAsyncThunk<
    void,
    { documentId: AppId; payload: OrderDocumentDqMappingsAiGenerationRequestDto },
    AppThunkAPIType
>(
    "document-dq-mappings/runDocumentDqMappingsAiGenerationProcess",
    async ({ documentId, payload }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(postWithAuth({
            url: `api/document-dq-mappings/documents/${documentId}/ai/generate-mappings/process`,
            payload,
        }));

        if (postWithAuth.fulfilled.match(result)) {
            return;
        }

        return rejectWithValue(result.payload ?? { kind: "unknown" });
    }
)


export interface CreateDocumentDqMappingPayload {
    requirementNumber: string;
    requirementDescription?: string;
    fsNumber: string;
    fsTitle: string;
    fsDescription: string;
    mappingComment: string;
    note: DqMappingNote | null;
}

export const createDocumentDqMapping = createAsyncThunk<
    DocumentDqMapping,
    { documentId: AppId; sectionId: string; payload: CreateDocumentDqMappingPayload },
    AppThunkAPIType
>(
    "document-dq-mappings/createDocumentDqMapping",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/document-dq-mappings/${data.documentId}/sections/${data.sectionId}`,
                payload: { ...data.payload },
            })
        );

        if (postWithAuth.fulfilled.match(result)) {
            return result.payload as DocumentDqMapping;
        } else {
            return rejectWithValue(result.payload ?? { kind: "unknown" });
        }
    }
);


export interface UpdateDocumentDqMappingPayload {
    requirementNumber: string;
    requirementDescription?: string;
    fsNumber: string;
    fsTitle: string;
    fsDescription: string;
    mappingComment: string;
    note: DqMappingNote | null;
}

export const updateDocumentDqMapping = createAsyncThunk<
    DocumentDqMapping,
    { documentId: AppId; sectionId: string; id: number; payload: UpdateDocumentDqMappingPayload },
    AppThunkAPIType
>(
    "document-dq-mappings/updateDocumentDqMapping",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            putWithAuth({
                url: `api/document-dq-mappings/${data.documentId}/sections/${data.sectionId}/${data.id}`,
                payload: { ...data.payload },
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as DocumentDqMapping;
        }
        return rejectWithValue(result.payload ?? { kind: "unknown" });
    }
);


export interface UpdateDqMappingReport {
    note: DqMappingNote | null;
    mappingComment: string;
    documents: DqMappingDocumentItem[];
}

export const updateDqMappingForReport = createAsyncThunk<
    DocumentDqMapping,
    { documentId: AppId; sectionId: string; id: number; payload: UpdateDqMappingReport },
    AppThunkAPIType
>(
    "document-dq-mappings/updateDqMappingForReport",
    async (data, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            putWithAuth({
                url: `api/document-dq-mappings/${data.documentId}/sections/${data.sectionId}/reports/dq-mappings/${data.id}`,
                payload: { ...data.payload },
            })
        );

        if (putWithAuth.fulfilled.match(result)) {
            return result.payload as DocumentDqMapping;
        }
        return rejectWithValue(result.payload ?? { kind: "unknown" });
    }
);

export const deleteDqMapping = createAsyncThunk<number, { documentId: AppId, sectionId: string, mappingId: number; }, AppThunkAPIType>("document-questions/deleteDocumentSectionQuestion",
    async (data, { dispatch, rejectWithValue }) => {

        const result = await dispatch(deleteWithAuth({
            url: `api/document-dq-mappings/${data.documentId}/sections/${data.sectionId}/${data.mappingId}`,
        }))
        const { payload } = result

        if (deleteWithAuth.fulfilled.match(result)) {
            return data.mappingId

        } else {
            return rejectWithValue(payload ?? { kind: 'unknown' })
        }
    })
